/**
 * Creating plans
 */
export const CREATE_COMMUNITY_PLAN_MESSAGE =
  "Before you can create a new Plan, you must configure at least one Master Plan.";

/**
 * Deleting plans
 */
export const DELETE_STUDIO_DOC_MESSAGE =
  "Deleting this Studio Document will destroy all associated Master Plans, Plans, Home Plans, and related assets.";
export const DELETE_MASTER_PLAN_MESSAGE =
  "Deleting this Master Plan will destroy all associated Plans, Home Plans, and related assets for this builder.";
export const DELETE_COMMUNITY_PLAN_MESSAGE = "Deleting this Plan will destroy all associated Home Plans.";
export const DELETE_HOME_PLAN_MESSAGE =
  "Deleting this Home Plan will also delete its corresponding lot-specific Studio file.";
export const DELETE_HOME_PLAN_MULTI_UNIT_MESSAGE =
  "This Home Plan is assigned to the multi-unit aggregated plan. Deleting it will remove this unit's choices from the aggregated plan.";
export const DELETE_MULTI_UNIT_HOME_PLAN_TOOLTIP =
  "This whole building Home Plan can only be deleted by deleting the associated location Plan.";
export const DELETE_HOME_PLANS_MESSAGE =
  "Deleting these Home Plans will also delete their corresponding lot-specific Studio files.";

/**
 * Detaching plans
 */
export const DETACH_MULTI_UNIT_HOME_PLAN_TOOLTIP = "Detaching is not yet supported for multi-unit home plans.";

/**
 * Locking plans
 */
export const LOCK_HOME_PLAN_PREP_TOOLTIP = "Home plans cannot be locked in the Plan Preparation phase.";
export const LOCK_UNIT_HOME_PLAN_LOT_AVAILABLE_TOOLTIP =
  "Unit Home Plans cannot be locked while the lot is still available.";
export const HOME_PLAN_LOCKING_IMPLICATIONS = [
  "Prevent future option configuration",
  "Prevent updating Home Plan information",
  "Downloadable assets, including the Plan Set PDF, will not change, even if the upstream plans are updated",
];

export const HOME_PLAN_UNLOCKING_IMPLICATIONS = [
  "Enable future option configuration",
  "Enable updating Home Plan information",
  "Downloadable assets, including the Plan Set PDF, will change as the upstream plans are updated",
];

export const BUILDING_LOCK_IMPLICATION = "Lock all unit-level Home Plans in the building";
export const BUILDING_UNLOCK_UNIT_HEADER = "Unlock Unit Home Plans too?";
export const BUILDING_UNLOCK_SCOPE_NONE_LABEL = "None (only unlocks the building Home Plan)";
export const UNLOCK_UNIT_HOME_PLAN_BUILDING_LOCKED_TOOLTIP =
  "Cannot unlock this unit while its building Home Plan is locked. Unlock the building first.";

export function buildingUnlockScopeAllLabel(totalUnitCount: number): string {
  const unitWord = totalUnitCount === 1 ? "unit" : "units";
  return `All ${totalUnitCount} ${unitWord}`;
}

export function buildingUnlockScopeMatchedLabel(matchedUnitCount: number): string {
  const unitWord = matchedUnitCount === 1 ? "unit" : "units";
  return `${matchedUnitCount} ${unitWord} locked when the building was locked`;
}

export function formatMatchedUnlockUnitList(
  units: ReadonlyArray<{ planSetId: string; reportingUnitName: string }>,
): string {
  return units.map((unit) => `${unit.planSetId} (${unit.reportingUnitName})`).join(", ");
}

/**
 * Operations mode
 */
export const CONFIGURE_OPERATIONS_MULTI_UNIT_TOOLTIP =
  "Not supported for Multi-Unit locations. Use config overrides to set base options for the plan or location.";

/**
 * Downloads
 */
export const MULTI_UNIT_DOWNLOAD_DISABLED_TOOLTIP = "Not available yet for Home Plans in Multi-Unit locations.";

/**
 * Showroom links
 */
export const VIEW_SHOWROOM_FULL_BUILDING_MULTI_UNIT_TOOLTIP = "Not supported on the full building Multi-Unit Home Plan";

/**
 * Assigning plans to locations
 */
export const MULTI_UNIT_DISABLED_TOOLTIP =
  "Multi-unit plans must be added individually at the location to handle unit/lot associations.";
export const NO_MULTI_UNIT_PLANS_AVAILABLE_MESSAGE =
  "No multi-unit plans are available. Multi-unit locations only support plans with multiple units.";

export function noPlansMatchingSearchMessage(locationPlanName: string): string {
  return `There aren't any ${locationPlanName}s matching your search.`;
}

/**
 * Creating home plans
 */
export const MULTI_UNIT_PLAN_AT_NON_MULTI_UNIT_LOCATION = "Multi-unit plans can only be added at multi-unit locations.";
export const LOCATION_PLAN_NOT_AVAILABLE_AT_LOT_LOCATION =
  "Plans can only be assigned to lots at the plan's own location or below it.";
export const MASTER_PLAN_UNRESOLVED_MESSAGE = "Unable to load plan unit information.";
export const MASTER_PLAN_UNRESOLVED_HOME_PLAN_MESSAGE =
  "Plan unit information could not be loaded for a plan at this location.";

/**
 * Showroom intro — public save CTA
 */
export const MULTI_UNIT_PLAN_SAVE_AT_NON_MULTI_UNIT_LOCATION = "This plan can only be saved in a multi-unit location.";

/**
 * Moving home plans
 */
export const HOME_PLANS_NOT_ALLOWED_TOOLTIP = "Home plans are not allowed at this location.";

/**
 * Archiving plans
 */
export const ARCHIVE_PLAN_MESSAGE =
  "This action also archives all children location plans. Any attached home plans will retain their data.";

/**
 * Multi-unit lot availability
 */
// Mirrors every check in `getUnitAssignmentIneligibilityReason`.
// Note: Stating lot status "available" as requirement here even though actual guard is looser... we might tighten
export const LOT_ELIGIBILITY_RULES =
  "A lot is eligible if it is available, has no home plans or lot-plan assignments, and is not already assigned to a unit in a multi-unit building.";
export const COULD_NOT_LOAD_LOTS_MESSAGE =
  "Lot availability could not be loaded, so plans cannot be checked against it. Close and reopen to try again.";
export const CHECKING_LOT_AVAILABILITY_MESSAGE = "Checking lot availability...";

/**
 * Integrations with other systems
 */
export const EXTERNAL_ID_OVERVIEW_MESSAGE =
  "This ID can be used to connect Higharc with external systems, such as an ERP or CRM.";
